import React, { useState, useEffect } from 'react';
import { obtenerCartas, actualizarCarta, eliminarCarta, actualizarClausula, eliminarClausula, agregarClausula } from '../../services/cartaCompromisoService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FileText, PlusCircle, Edit2, Trash2, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'cartas'
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'cartas'
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'cartas'
    });
};

const GestionCartas = () => {
    const navigate = useNavigate();
    const [cartas, setCartas] = useState([]);
    const [cartaExpandida, setCartaExpandida] = useState(null);
    const [clausulaEnEdicion, setClausulaEnEdicion] = useState(null);
    const [nuevaClausula, setNuevaClausula] = useState('');
    const [cartaEnEdicion, setCartaEnEdicion] = useState(null);

    useEffect(() => {
        cargarCartas();
    }, []);

    const cargarCartas = async () => {
        try {
            const response = await obtenerCartas();
            if (response.success) {
                setCartas(response.data);
            }
        } catch (error) {
            showErrorToast('Error al cargar las cartas de compromiso');
        }
    };

    const handleCrearCarta = () => {
        navigate('/admin/centro/cartas/crear');
    };

    const handleEditarCarta = (carta) => {
        setCartaEnEdicion({
            ...carta
        });
        setCartaExpandida(carta.id_carta);
    };

    const handleGuardarCarta = async () => {
        try {
            await actualizarCarta(cartaEnEdicion.id_carta, {
                nombre: cartaEnEdicion.nombre,
                descripcion: cartaEnEdicion.descripcion
            });
            setCartaEnEdicion(null);
            await cargarCartas();
            showSuccessToast('Carta actualizada exitosamente');
        } catch (error) {
            showErrorToast('Error al actualizar la carta');
        }
    };

    const handleEditarClausula = (clausula) => {
        console.log('Editando cláusula:', clausula); // Para depuración
        if (!clausula.id_carta) {
            showErrorToast('Error: ID de carta no encontrado');
            return;
        }
        setClausulaEnEdicion({
            id_clausula: clausula.id_clausula,
            clausula: clausula.clausula,
            id_carta: clausula.id_carta
        });
    };

    const handleGuardarClausula = async (clausula) => {
        if (!clausula.clausula.trim()) {
            showWarningToast('Por favor, ingrese el contenido de la cláusula');
            return;
        }
        
        if (clausula.clausula.length > 500) {
            showWarningToast('La cláusula no puede exceder los 500 caracteres');
            return;
        }
        
        try {
            await actualizarClausula(
                clausula.id_carta,
                clausula.id_clausula,
                { clausula: clausula.clausula }
            );
            setClausulaEnEdicion(null);
            await cargarCartas();
            showSuccessToast('Cláusula actualizada exitosamente');
        } catch (error) {
            console.error('Error detallado:', error);
            showErrorToast('Error al actualizar la cláusula');
        }
    };

    const handleEliminarClausula = async (clausula) => {
        try {
            await eliminarClausula(clausula.id_carta, clausula.id_clausula);
            await cargarCartas();
            showSuccessToast('Cláusula eliminada exitosamente');
        } catch (error) {
            showErrorToast('Error al eliminar la cláusula');
        }
    };

    const handleAgregarClausula = async (carta) => {
        if (!nuevaClausula.trim()) {
            showWarningToast('Por favor, ingrese el contenido de la cláusula');
            return;
        }
        
        if (nuevaClausula.length > 500) {
            showWarningToast('La cláusula no puede exceder los 500 caracteres');
            return;
        }

        try {
            await agregarClausula(carta.id_carta, { clausula: nuevaClausula });
            setNuevaClausula('');
            await cargarCartas();
            showSuccessToast('Cláusula agregada exitosamente');
        } catch (error) {
            console.error('Error detallado:', error);
            showErrorToast('Error al agregar la cláusula');
        }
    };

    const confirmarEliminacion = (carta) => {
        toast.warn(
            <div>
                <p className="font-bold mb-2">¿Está seguro de eliminar esta carta de compromiso?</p>
                <p className="mb-4">Esta acción no se puede deshacer.</p>
                <div className="flex justify-end space-x-2">
                    <button
                        onClick={() => toast.dismiss()}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                await eliminarCarta(carta.id_carta);
                                await cargarCartas();
                                showSuccessToast('Carta de compromiso eliminada exitosamente');
                            } catch (error) {
                                showErrorToast('Error al eliminar la carta de compromiso');
                            }
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Eliminar
                    </button>
                </div>
            </div>,
            {
                position: "top-center",
                autoClose: false,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: false,
                closeButton: false,
                className: "rounded-lg",
                containerId: "cartas"
            }
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-6">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="bg-indigo-100 p-3 rounded-xl mr-4">
                                <FileText className="h-8 w-8 text-indigo-600" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-1">Gestión de Cartas de Compromiso</h1>
                                <p className="text-gray-600">Administre las cartas de compromiso del centro educativo</p>
                            </div>
                        </div>
                        <button
                            onClick={handleCrearCarta}
                            className="flex items-center px-6 py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            <PlusCircle className="w-5 h-5 mr-2" />
                            Nueva Carta
                        </button>
                    </div>
                </div>

                <div className="space-y-6">
                    {cartas.map((carta) => (
                        <div key={carta.id_carta} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-200">
                            <div className="flex justify-between items-center">
                                <div className="flex-1">
                                    {cartaEnEdicion?.id_carta === carta.id_carta ? (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Carta</label>
                                                <input
                                                    type="text"
                                                    value={cartaEnEdicion.nombre}
                                                    onChange={(e) => setCartaEnEdicion({
                                                        ...cartaEnEdicion,
                                                        nombre: e.target.value
                                                    })}
                                                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-all duration-200"
                                                    placeholder="Ingrese el nombre de la carta"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                                                <textarea
                                                    value={cartaEnEdicion.descripcion}
                                                    onChange={(e) => setCartaEnEdicion({
                                                        ...cartaEnEdicion,
                                                        descripcion: e.target.value
                                                    })}
                                                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-all duration-200 resize-none"
                                                    rows="3"
                                                    placeholder="Ingrese la descripción de la carta"
                                                />
                                            </div>
                                            <div className="flex justify-end space-x-3 pt-2">
                                                <button
                                                    onClick={() => setCartaEnEdicion(null)}
                                                    className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    onClick={handleGuardarCarta}
                                                    className="px-4 py-2 text-sm bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200 font-medium shadow-sm"
                                                >
                                                    Guardar Cambios
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center">
                                            <div className="bg-indigo-50 p-2 rounded-lg mr-3">
                                                <FileText className="w-5 h-5 text-indigo-600" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900">{carta.nombre}</h3>
                                        </div>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    {!cartaEnEdicion && (
                                        <button
                                            onClick={() => handleEditarCarta(carta)}
                                            className="p-3 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 hover:scale-105"
                                            title="Editar carta"
                                        >
                                            <Edit2 className="w-5 h-5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => confirmarEliminacion(carta)}
                                        className="p-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 hover:scale-105"
                                        title="Eliminar carta"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setCartaExpandida(cartaExpandida === carta.id_carta ? null : carta.id_carta)}
                                        className="p-3 text-gray-600 hover:bg-gray-50 rounded-xl transition-all duration-200 hover:scale-105"
                                        title={cartaExpandida === carta.id_carta ? "Contraer" : "Expandir"}
                                    >
                                        {cartaExpandida === carta.id_carta ? (
                                            <ChevronUp className="w-5 h-5" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            {cartaExpandida === carta.id_carta && !cartaEnEdicion && (
                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                                        <h4 className="font-medium text-gray-700 mb-2">Descripción:</h4>
                                        <p className="text-gray-600 leading-relaxed">{carta.descripcion}</p>
                                    </div>
                                    <div className="space-y-4">
                                        <h4 className="text-lg font-bold text-gray-900 flex items-center">
                                            <div className="w-1 h-6 bg-indigo-500 rounded-full mr-3"></div>
                                            Cláusulas
                                        </h4>
                                        <div className="space-y-2">
                                            {carta.clausulas.map((clausula) => {
                                                // Asignar el id_carta de la carta padre a la cláusula
                                                clausula.id_carta = carta.id_carta;
                                                return (
                                                    <div key={clausula.id_clausula} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200">
                                                        {clausulaEnEdicion?.id_clausula === clausula.id_clausula ? (
                                                            <div className="space-y-3">
                                                                <label className="block text-sm font-medium text-gray-700">Editar Cláusula</label>
                                                                <textarea
                                                                    value={clausulaEnEdicion.clausula}
                                                                    onChange={(e) => setClausulaEnEdicion({
                                                                        ...clausulaEnEdicion,
                                                                        clausula: e.target.value,
                                                                        id_carta: clausulaEnEdicion.id_carta
                                                                    })}
                                                                    maxLength={500}
                                                                    className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-all duration-200 resize-none"
                                                                    rows="4"
                                                                    placeholder="Escriba el contenido de la cláusula..."
                                                                />
                                                                <div className="flex justify-between items-center">
                                                                    <div className="text-sm text-gray-500">
                                                                        {clausulaEnEdicion.clausula.length}/500 caracteres
                                                                    </div>
                                                                    <div className="flex space-x-2">
                                                                        <button
                                                                            onClick={() => setClausulaEnEdicion(null)}
                                                                            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                                                                        >
                                                                            Cancelar
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleGuardarClausula(clausulaEnEdicion)}
                                                                            className="px-4 py-2 text-sm bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200 font-medium shadow-sm"
                                                                        >
                                                                            Guardar
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-start justify-between">
                                                                <p className="flex-1 text-gray-700 leading-relaxed pr-4">{clausula.clausula}</p>
                                                                <div className="flex space-x-1 flex-shrink-0">
                                                                    <button
                                                                        onClick={() => handleEditarClausula(clausula)}
                                                                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 hover:scale-105"
                                                                        title="Editar cláusula"
                                                                    >
                                                                        <Edit2 className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleEliminarClausula(clausula)}
                                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-105"
                                                                        title="Eliminar cláusula"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="mt-6">
                                            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 space-y-3">
                                                <h5 className="font-medium text-gray-800">Agregar Nueva Cláusula</h5>
                                                <div className="space-y-3">
                                                    <textarea
                                                        value={nuevaClausula}
                                                        onChange={(e) => setNuevaClausula(e.target.value)}
                                                        placeholder="Escriba el contenido de la nueva cláusula..."
                                                        maxLength={500}
                                                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm transition-all duration-200 resize-none"
                                                        rows="3"
                                                    />
                                                    <div className="flex justify-between items-center">
                                                        <div className="text-sm text-gray-500">
                                                            {nuevaClausula.length}/500 caracteres
                                                        </div>
                                                        <button
                                                            onClick={() => handleAgregarClausula(carta)}
                                                            disabled={!nuevaClausula.trim()}
                                                            className={`px-6 py-2 rounded-xl font-medium transition-all duration-200 shadow-sm ${
                                                                nuevaClausula.trim() 
                                                                    ? 'bg-indigo-500 text-white hover:bg-indigo-600 hover:shadow-md transform hover:-translate-y-0.5' 
                                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            }`}
                                                        >
                                                            Agregar Cláusula
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <ToastContainer 
                position="bottom-right" 
                toastClassName="rounded-xl shadow-lg"
                bodyClassName="font-medium"
            />
        </div>
    );
};

export default GestionCartas;